import { useState } from "react";
import { rpc, useQuote, isUuid, todaySP, bookingWaMessage, fmtDay } from "@/lib/booking";
import { waLinkFrom } from "@/lib/siteData";
import { AvailabilityCalendar } from "./AvailabilityCalendar";
import { ParticipantsPicker } from "./ParticipantsPicker";
import { AddonsPicker, useAddons } from "./AddonsPicker";
import { CustomerForm } from "./CustomerForm";
import { PriceSummary } from "./PriceSummary";
import { input, label, panel } from "./ui";

const EMPTY_CUSTOMER = { name: "", phone: "", email: "", birthdate: "", notes: "", consent: false };

export function BookingFlow({ tour, settings, initial, onDone }) {
    const mode = tour.booking_enabled ? tour.booking_type || "scheduled" : "disabled";
    const [slot, setSlot] = useState(null);
    const [date, setDate] = useState(initial.date || "");
    const [people, setPeople] = useState({ adults: Math.max(1, Math.min(initial.guests || 1, tour.max_guests || 10)), children_paid: 0, children_free: 0 });
    const [extras, setExtras] = useState({});
    const [coupon, setCoupon] = useState("");
    const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const addons = useAddons(tour.id);
    const guests = people.adults + people.children_paid + people.children_free;
    const payload = {
        tour_id: tour.id, slot_id: slot?.slot_id || null, date: mode === "scheduled" ? null : date || null, ...people, coupon: coupon || null,
        addons: Object.entries(extras).filter(([, q]) => q > 0).map(([id, qty]) => ({ id, qty })),
    };
    const ready = isUuid(tour.id) && mode !== "disabled" && (mode === "scheduled" ? Boolean(slot) : mode === "whatsapp" || Boolean(date));
    const { quote, loading, error: quoteError } = useQuote(payload, ready);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (mode === "whatsapp") {
            // Esta modalidade é atendimento humano, NÃO é confirmação ou cobrança.
            // O backend existente recusa intencionalmente create_booking nesta modalidade.
            // Não transmitimos e-mail/data de nascimento na mensagem WhatsApp.
            const message = bookingWaMessage({
                tour,
                when: date ? fmtDay(date) + " (a confirmar)" : "Data a combinar",
                adults: people.adults,
                kids: people.children_paid + people.children_free,
                quote: quote && !quoteError && !loading ? quote : null,
                name: customer.name,
                notes: customer.notes,
            }) + "\nWhatsApp para retorno: " + customer.phone;
            window.location.assign(waLinkFrom(settings, message));
            return;
        }
        // Não confiar em orçamento anterior durante nova seleção.
        if (!ready || !quote || loading || quoteError) {
            setError("Confira a data, o horário e os valores antes de solicitar.");
            return;
        }
        setBusy(true);
        try {
            onDone(await rpc("create_booking", { p: { ...payload, ...customer } }), customer);
        } catch (ex) {
            setError(ex.message || "Não foi possível enviar a solicitação.");
        } finally {
            setBusy(false);
        }
    };

    if (mode === "disabled") return <div className={panel + " mt-8"}><h2 className="font-display text-xl font-bold">Agendamento ainda indisponível</h2><p className="mt-3 text-sm text-dim">A equipe ainda não liberou horários reais para esta experiência. Consulte a disponibilidade antes de se deslocar.</p><a href={waLinkFrom(settings, `Olá! Gostaria de consultar a disponibilidade do passeio: ${tour.title}.`)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-12 items-center justify-center rounded-full border border-neon-wa/50 px-5 text-sm font-bold text-neon-wa">Consultar pelo WhatsApp</a></div>;

    return (
        <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <form onSubmit={submit} className={panel + " space-y-6"} data-testid="booking-form">
                <h2 className="font-display text-xl font-bold">Reservar</h2>
                {mode === "scheduled" && <AvailabilityCalendar tourId={tour.id} initialDate={initial.date} value={slot} onChange={setSlot} guests={guests} />}
                {mode !== "scheduled" && <div>
                    <label htmlFor="booking-date" className={label}>Data desejada {mode === "flexible" ? "(a combinar com a equipe)" : ""}</label>
                    <input id="booking-date" data-testid="booking-date" type="date" min={todaySP()} className={input} value={date} onChange={(e) => setDate(e.target.value)} required={mode === "flexible"} />
                </div>}
                <ParticipantsPicker tour={tour} value={people} onChange={setPeople} maxAvailable={slot?.available} />
                <AddonsPicker addons={addons} value={extras} onChange={setExtras} />
                <div>
                    <label htmlFor="booking-coupon" className={label}>Cupom de desconto</label>
                    <input id="booking-coupon" data-testid="booking-coupon" className={input} maxLength={24} value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase().trim())} placeholder="CÓDIGO" />
                </div>
                <CustomerForm value={customer} onChange={setCustomer} />
                {error && <p role="alert" className="text-sm text-neon-coral" data-testid="booking-error">{error}</p>}
                <button data-testid="booking-submit" disabled={busy || (mode !== "whatsapp" && (!ready || !quote || loading || Boolean(quoteError))) || (mode !== "whatsapp" && coupon && (!quote || quote.coupon_status !== "valid"))}
                    className="w-full rounded-full bg-neon-cyan px-6 py-4 font-display text-xs font-bold uppercase tracking-widest text-ocean disabled:opacity-40">
                    {busy ? "Enviando..." : mode === "whatsapp" ? "Solicitar pelo WhatsApp" : "Solicitar reserva"}
                </button>
                <p className="text-xs text-dim">{mode === "whatsapp" ? "Esta modalidade envia uma mensagem à equipe. Não confirma vaga nem gera cobrança automaticamente." : "Valores, vagas e cupons são validados no servidor. A reserva só é definitiva após confirmação da equipe."}</p>
            </form>
            <PriceSummary tour={tour} slot={slot} date={mode === "scheduled" ? "" : date} guests={guests} quote={quote} loading={loading} error={quoteError} couponCode={coupon} />
        </div>
    );
}
